import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import type { TaskingBotTaskStatus, TaskingBotTaskPriority } from './schema';

export interface TaskingBotTask {
  id: string;
  userId: number;
  title: string;
  description: string | null;
  status: TaskingBotTaskStatus;
  priority: TaskingBotTaskPriority;
  project: string | null;
  projectId: string | null;
  parentTaskId?: string | null;
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  dueDate: string | null;
  dueTime: string | null; // Format: "HH:MM" (24-hour)
  cronExpression: string | null;
  cronTimezone: string | null;
  tags: string; // JSON array
  metadata: string; // JSON object
  position: number;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
}

const QUERY_KEY = ['taskingbot', 'tasks'];

export function useTaskingBotTasks(filters?: {
  status?: string;
  priority?: string;
  projectId?: string;
  limit?: number;
}) {
  const query = useQuery({
    queryKey: [...QUERY_KEY, filters],
    queryFn: async (): Promise<TaskingBotTask[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.projectId) params.set('projectId', filters.projectId);
      if (filters?.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const url = `/_api/taskingbot/tasks${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch tasks');
      const data = superjson.parse<{ tasks: TaskingBotTask[] }>(await res.text());
      return data.tasks;
    },
    staleTime: 5000,
    // Auto-refresh: 4s when tasks are running, 15s baseline to detect new tasks
    refetchInterval: (query: any) => {
      const tasks = query?.state?.data;
      const hasRunning = Array.isArray(tasks) && tasks.some((t: any) => t.status === 'in_progress');
      return hasRunning ? 4000 : 15000;
    },
  });
  return query;
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      status?: TaskingBotTaskStatus;
      priority?: TaskingBotTaskPriority;
      projectId?: string | null;
      parentTaskId?: string | null;
      position?: number;
      startDate?: string | null;
      startTime?: string | null;
      endDate?: string | null;
      endTime?: string | null;
      dueDate?: string | null;
      dueTime?: string | null;
      cronExpression?: string | null;
      cronTimezone?: string | null;
      title?: string;
    }) => {
      const res = await fetch('/_api/taskingbot/tasks', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(payload),
      });
      const text = await res.text();
      let data: any;
      try {
        data = superjson.parse(text);
      } catch {
        data = JSON.parse(text);
      }
      if (!res.ok) throw new Error(data?.error || `Update failed (${res.status})`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/_api/taskingbot/tasks?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const text = await res.text();
      let data: any;
      try {
        data = superjson.parse(text);
      } catch {
        data = JSON.parse(text);
      }
      if (!res.ok) throw new Error(data?.error || `Delete failed (${res.status})`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
    },
  });
}

export function useCreateTaskingBotTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      title: string;
      description?: string | null;
      projectId?: string | null;
      parentTaskId?: string | null;
      subtasks?: string[];
      workflowId?: string | null;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      participants?: string[];
    }) => {
      const res = await fetch('/_api/taskingbot/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(payload),
      });
      const text = await res.text();
      let data: any;
      try {
        data = superjson.parse(text);
      } catch {
        data = JSON.parse(text);
      }
      if (!res.ok) throw new Error(data?.error || `Create failed (${res.status})`);
      return data as { success: boolean; id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
    },
  });
}

export function useReorderTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskIds: string[]; status: string }) => {
      const res = await fetch('/_api/taskingbot/tasks/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to reorder tasks');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

// ── Fast-polling hook for currently running tasks (TaskPlex real-time display) ──
export interface RunningTaskMeta {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  cronExpression: string | null;
  progress: number;
  startTime: string | null;
  endTime: string | null;
  result: string;
  needs: string[];
  events: Array<{ at: string; phase: string; status: string; message: string }>;
  lastRunDetails: string[];
  currentStep: string | null;
  executionStatus: string | null;
  lastToolCalls: number;
  lastRunStatus: string | null;
  lastRunSummary: string | null;
  lastRunAt: string | null;
  lastModel: string | null;
  runCount: number;
  tokensUsed: number;
  skillsUsed: string[];
  participants: string[];
}

export function useRunningTasks() {
  return useQuery({
    queryKey: ['taskingbot', 'running-tasks'],
    queryFn: async (): Promise<RunningTaskMeta[]> => {
      const res = await fetch('/_api/taskingbot/running-tasks', { credentials: 'include' });
      if (!res.ok) return [];
      const text = await res.text();
      try {
        return superjson.parse<RunningTaskMeta[]>(text);
      } catch {}
      try {
        return JSON.parse(text);
      } catch {}
      return [];
    },
    refetchInterval: 10000,
    staleTime: 8000,
  });
}

// ── Task conversation thread (polls when detail panel is open) ──
export function useTaskMessages(taskId: string | null) {
  return useQuery({
    queryKey: ['taskingbot', 'task-messages', taskId],
    queryFn: async () => {
      if (!taskId) return [];
      const res = await fetch(
        `/_api/taskingbot/task-messages?taskId=${encodeURIComponent(taskId)}`,
        { credentials: 'include' },
      );
      if (!res.ok) return [];
      const text = await res.text();
      let data: any;
      try {
        data = superjson.parse(text);
      } catch {
        try {
          data = JSON.parse(text);
        } catch {
          return [];
        }
      }
      return data.messages || data || [];
    },
    enabled: !!taskId,
    refetchInterval: 30000,
    staleTime: 25000,
  });
}

export function useExecuteTaskingBotTaskNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: string | { taskId: string; force?: boolean }) => {
      const taskId = typeof input === 'string' ? input : input.taskId;
      const force = typeof input === 'string' ? false : input.force;
      const endpoints = ['/_api/taskingbot/tasks-execute-now', '/_api/taskingbot/tasks/execute'];
      for (const url of endpoints) {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: superjson.stringify({ taskId, force }),
        });
        if (res.status === 404) continue;
        const text = await res.text();
        let data: any;
        try {
          data = superjson.parse(text);
        } catch {
          try {
            data = JSON.parse(text);
          } catch {
            data = null;
          }
        }
        if (!res.ok) throw new Error(data?.error || `Execute failed (${res.status})`);
        return data;
      }
      throw new Error('Task execute endpoint not found');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] }),
  });
}
